import React, { useContext } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useMutation, useQuery } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';
import { Info } from '@phosphor-icons/react';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { SingleCheckbox } from '../../../components/Checkbox';
import UserContext from '../../../contexts/UserContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import { statusColorScheme } from '../../AccessPoints/List/Card';
import { getManualHubCounterStatusTargets, HubCounterManualStatus } from '../Tabs/hubCounterState';
import {
  CONFIRM_HUB_COUNTER,
  ConfirmHubCounterMutation,
  GET_SUGGESTED_HUB_COUNTER_ID,
  REJECT_HUB_COUNTER,
  RejectHubCounterMutation,
  SuggestedHubCounterIdQuery,
  UPDATE_HUB_COUNTER_STATUS,
} from '../query';

const ChangeHubCounterStatusSchema = Yup.object().shape({
  status: Yup.string().required('Du musst einen Status auswählen'),
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

interface ChangeHubCounterStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  currentStatus?: string | null;
  initialHubCounterId?: string;
  refetch: () => Promise<any> | void;
  refetchState?: () => Promise<any> | void;
}

const statusOptions = (targets: HubCounterManualStatus[]) => [
  {
    label: 'Status auswählen',
    value: '',
    disabled: true,
  },
  ...targets.map((status) => ({
    label: status,
    value: status,
  })),
];

const ChangeHubCounterStatusModal = ({
  isOpen,
  onClose,
  dropPointId,
  currentStatus,
  initialHubCounterId,
  refetch,
  refetchState,
}: ChangeHubCounterStatusModalProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const targets = getManualHubCounterStatusTargets(currentStatus);
  const canSuggestHubCounterId = currentStatus === 'PENDING' || currentStatus === 'WAITLIST';
  const { data, loading: suggestedLoading } = useQuery<SuggestedHubCounterIdQuery>(GET_SUGGESTED_HUB_COUNTER_ID, {
    variables: { dropPointId },
    skip: !isOpen || !canSuggestHubCounterId,
    fetchPolicy: 'cache-and-network',
  });
  const [updateHubCounterStatus] = useMutation(UPDATE_HUB_COUNTER_STATUS);
  const [confirmHubCounter] = useMutation<ConfirmHubCounterMutation>(CONFIRM_HUB_COUNTER);
  const [rejectHubCounter] = useMutation<RejectHubCounterMutation>(REJECT_HUB_COUNTER);

  const refetchAll = async () => {
    await Promise.all([Promise.resolve(refetch()), Promise.resolve(refetchState?.())]);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Hub Counter Status ändern</ModalHeader>
        <Formik
          enableReinitialize
          initialValues={{
            status: '',
            hubCounterId: initialHubCounterId || data?.suggestedHubCounterId || '',
            skipFeedCreation: false,
            comment: '',
          }}
          validationSchema={targets.length ? ChangeHubCounterStatusSchema : undefined}
          onSubmit={async ({ status: nextStatus, hubCounterId, skipFeedCreation, comment }) => {
            try {
              toggleSpinner(true);
              if (nextStatus === 'CONFIRMED') {
                await confirmHubCounter({
                  variables: {
                    dropPointId,
                    hubCounterId: hubCounterId.trim() || undefined,
                    skipFeedCreation: skipFeedCreation || undefined,
                    comment,
                    internalUserId: internalUser.id,
                  },
                });
              } else if (nextStatus === 'REJECTED') {
                await rejectHubCounter({
                  variables: {
                    dropPointId,
                    reason: comment.trim(),
                    internalUserId: internalUser.id,
                  },
                });
              } else {
                await updateHubCounterStatus({
                  variables: {
                    dropPointId,
                    status: nextStatus,
                    reason: comment,
                    internalUserId: internalUser.id,
                  },
                });
              }
              await refetchAll();
              toast({
                title: 'Erfolg!',
                description:
                  nextStatus === 'CONFIRMED'
                    ? skipFeedCreation
                      ? 'Amazon Hub Counter bestätigt ohne CREATE-Feed. UPS bleibt unverändert.'
                      : 'Amazon Hub Counter wurde bestätigt. UPS bleibt unverändert.'
                    : nextStatus === 'REJECTED'
                      ? 'Hub Counter wurde abgelehnt.'
                      : `Hub Counter Status wurde auf ${nextStatus} gesetzt.`,
                status: 'success',
              });
              onClose();
            } catch (error) {
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Fehler beim Ändern des Status.'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          {({ values }) => (
            <Form>
              <ModalBody>
                <Text fontSize="sm" mb="2">
                  Aktueller Status
                </Text>
                <Badge colorScheme={statusColorScheme[currentStatus || ''] ?? 'gray'} mb="4">
                  {currentStatus || 'UNKNOWN'}
                </Badge>
                {targets.length ? (
                  <>
                    <Select name="status" label="Status" options={statusOptions(targets)} />
                    {values.status === 'CONFIRMED' ? (
                      <>
                        <Text fontSize="sm" mb="3">
                          Hier wird der DropPoint als Amazon Hub Counter im DropFriends-System angelegt und aktiviert. Das hat keinen
                          Einfluss auf das UPS Access Point Programm.
                        </Text>
                        <Text fontSize="sm" mb="4">
                          Zum Anlegen wird eine Hub Counter ID benötigt. Diese wird als eindeutige Bezeichnung des DropPoints in den
                          Systemen von Amazon und DropFriends verwendet. Ohne Eingabe verwendet das Backend automatisch die nächste freie
                          ID.
                        </Text>
                        <Input name="hubCounterId" label="Hub Counter ID" />
                        <Text fontSize="xs" color="gray.500" mt="2" mb="4">
                          {suggestedLoading
                            ? 'Vorschlag wird geladen...'
                            : data?.suggestedHubCounterId
                              ? `Backend-Vorschlag: ${data.suggestedHubCounterId}`
                              : 'Es liegt aktuell kein Vorschlag vor.'}
                        </Text>
                        <SingleCheckbox
                          name="skipFeedCreation"
                          label={
                            <Flex align="center" gap="2">
                              <Text fontSize="sm">DropPoint-Daten nicht automatisch an Amazon übertragen.</Text>
                              <Tooltip
                                placement="auto-start"
                                label="Wenn Du die Checkbox aktivierst, dann muss die Übertragung der DropPoint-Daten anschließend manuell durch Osamah per API erfolgen. Alternativ kann eine CSV mit den DropPoint-Daten an Amazon gesendet werden."
                                aria-label="Hinweis zur manuellen Übertragung der DropPoint-Daten"
                              >
                                <Box onClick={(event) => event.preventDefault()}>
                                  <Info />
                                </Box>
                              </Tooltip>
                            </Flex>
                          }
                        />
                      </>
                    ) : null}
                    <Input name="comment" label="Kommentar/Begründung" />
                  </>
                ) : (
                  <Text fontSize="sm">Dieser Status kann nicht geändert werden.</Text>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                  {targets.length ? 'Abbrechen' : 'Schließen'}
                </Button>
                {targets.length ? (
                  <Button ml="4" size="sm" colorScheme="brand" type="submit">
                    Speichern
                  </Button>
                ) : null}
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default ChangeHubCounterStatusModal;
